import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Container, Row, Col, Modal, Badge, OverlayTrigger, Tooltip, Table } from "react-bootstrap";
import "../componentCss/webform.css";
import "../componentCss/sidebar.css";
import {
  getFormListAction,
  deleteWebFormAction,
  updateWebFormAction,
  getDepartmentAction,
  submitWebFormActionData
} from "../../Action/modernAdminActions";
import "react-toastify/dist/ReactToastify.css";
import { Link } from "react-router-dom";
import config from "../../config/config";
import Swal from "sweetalert2";
import toast, { Toaster } from 'react-hot-toast';
import CustomPagination from '../common/CustomPagination';

import ReactToPrint, { useReactToPrint } from "react-to-print";
import { MdDelete, MdEdit, MdAdd, MdPrint, MdSearch, MdRefresh, MdVisibility, MdClear, MdDownload, MdUpload } from "react-icons/md";
import { debounce } from 'lodash';
import { BsPlus } from "react-icons/bs";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { BranchesOutlined } from "@ant-design/icons";

// Import version management component
import FormVersionSelector from './FormVersionSelector';
import formTemplateService from '../../services/formTemplateService';
import dynamicFormsService from '../../services/dynamicFormsService';

// Import export/import components
import ExportFormModal from './ExportFormModal';
import ImportFormModal from './ImportFormModal';

const Webform = () => {
  const [formList, setFormList] = useState([]);
  const [totalForms, setTotalForms] = useState(0);
  const [currentPage, setCurrentPage] = useState(1); // Change to 1-based pagination
  const [searchBy, setSearchBy] = useState('');
  const [searchByDepartment, setSearchByDepartment] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10); // Rename for clarity
  const [departmentList, setDepartmentList] = useState([])
  const [formActionData, setformActionData] = useState('')
  const [isModelView, setIsModelView] = useState(false);

  // Version management state
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [selectedFormForVersions, setSelectedFormForVersions] = useState(null);
  const [formVersionInfo, setFormVersionInfo] = useState({});
  const [selectedVersions, setSelectedVersions] = useState({}); // Track selected version for each form

  // Export/Import state
  const [selectedForms, setSelectedForms] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const [isLoading, setIsLoading] = useState(false); // Rename for clarity
  const allDataRef = useRef();

  // New clean pagination handler
  const handlePageChange = async (newPage) => {
    console.log('🔄 CUSTOM PAGINATION - Page change requested:', newPage);
    console.log('📍 Current page before change:', currentPage);
    console.log('📍 Items per page:', itemsPerPage);

    if (newPage === currentPage || isLoading) {
      console.log('❌ Skipping page change - same page or loading');
      return;
    }

    try {
      setIsLoading(true);
      setCurrentPage(newPage);

      // CLEAR PREVIOUS FORMS TO PREVENT MIXING OLD AND NEW DATA
      setFormList([]);
      console.log('🧹 Cleared previous form list');

      // Calculate skip value (0-based for API)
      const skip = (newPage - 1) * itemsPerPage;

      const params = {
        search: searchBy.trim().length >= 2 ? searchBy : '',
        department_id: searchByDepartment,
        skip: skip,
        limit: itemsPerPage
      };

      console.log('🚀 MAKING PAGINATION API CALL');
      console.log('📦 API Parameters:', params);
      console.log('🧮 Pagination calc:', {
        newPage,
        itemsPerPage,
        skip,
        limit: itemsPerPage
      });

      const res = await getFormListAction(params);
      console.log('📥 Pagination API Response:', res);

      if (res.success) {
        const formsData = res.data || [];
        const total = res.total || res.totalCount || res.count || 0;

        console.log('🔍 RAW API DATA:', {
          formsDataLength: formsData.length,
          total,
          firstForm: formsData[0],
          lastForm: formsData[formsData.length - 1]
        });

        const transformedForms = formsData.map(form => {
          const formId = form.form_id || form.id || form._id || form.formId;
          return {
            id: formId,
            form_id: formId,
            name: form.name || form.formName || 'Untitled Form',
            departmentName: form.department_name || 'Unknown',
            formNumber: form.form_number || form.formNumber || '',
            isLateEntry: form.isLateEntry || false,
            lateEntryTime: form.lateEntryTime || 0,
            is_active: form.is_active !== false
          };
        });

        console.log('🔄 TRANSFORMED FORMS FOR PAGE', newPage, ':', transformedForms);
        setFormList(transformedForms);
        setTotalForms(total);

        console.log('✅ PAGINATION SUCCESS:', {
          newPage,
          dataLength: transformedForms.length,
          total,
          showing: `${skip + 1}-${Math.min(skip + itemsPerPage, total)} of ${total}`
        });
      } else {
        console.log('❌ API failed:', res);
        setFormList([]);
        setTotalForms(0);
        toast.error(res.message || 'Failed to fetch forms');
      }
    } catch (error) {
      console.error('💥 Pagination error:', error);
      setFormList([]);
      setTotalForms(0);
      toast.error('Failed to load forms');
    } finally {
      setIsLoading(false);
    }
  };

  // New clean items per page handler
  const handleItemsPerPageChange = async (newItemsPerPage) => {
    console.log('🔄 ITEMS PER PAGE CHANGE:', newItemsPerPage);

    try {
      setIsLoading(true);
      setItemsPerPage(newItemsPerPage);
      setCurrentPage(1); // Reset to first page

      // CLEAR PREVIOUS FORMS TO PREVENT MIXING OLD AND NEW DATA
      setFormList([]);
      console.log('🧹 Cleared previous form list');

      const params = {
        search: searchBy.trim().length >= 2 ? searchBy : '',
        department_id: searchByDepartment,
        skip: 0,
        limit: newItemsPerPage
      };

      console.log('🚀 Items per page API call:', params);

      const res = await getFormListAction(params);

      if (res.success) {
        const formsData = res.data || [];
        const total = res.total || res.totalCount || res.count || 0;

        const transformedForms = formsData.map(form => {
          const formId = form.form_id || form.id || form._id || form.formId;
          return {
            id: formId,
            form_id: formId,
            name: form.name || form.formName || 'Untitled Form',
            departmentName: form.department_name || 'Unknown',
            formNumber: form.form_number || form.formNumber || '',
            isLateEntry: form.isLateEntry || false,
            lateEntryTime: form.lateEntryTime || 0,
            is_active: form.is_active !== false
          };
        });

        console.log('🔄 ITEMS PER PAGE - Setting forms:', transformedForms);
        setFormList(transformedForms);
        setTotalForms(total);
        console.log('✅ Items per page change success');
      } else {
        setFormList([]);
        setTotalForms(0);
        toast.error(res.message || 'Failed to fetch forms');
      }
    } catch (error) {
      console.error('Items per page error:', error);
      setFormList([]);
      setTotalForms(0);
      toast.error('Failed to load forms');
    } finally {
      setIsLoading(false);
    }
  };

  const getDepartmentList = useCallback(async () => {
    try {
      let res = await getDepartmentAction();
      if (res.success) {
        setDepartmentList(res.data);
      }
    } catch (error) {
      toast.error('Failed to load departments');
    }
  }, []);

  const handleOpenView = (data) => {
    setformActionData(data)
    setIsModelView(true);
  };

  const handleViewClose = () => {
    setIsModelView(false);
    setformActionData('');
  };

  // Simplified main data fetch function
  const fetchForms = useCallback(async (searchTerm = '', resetToFirstPage = false) => {
    try {
      setIsLoading(true);

      // CLEAR PREVIOUS FORMS TO PREVENT MIXING OLD AND NEW DATA
      setFormList([]);
      console.log('🧹 Cleared previous form list');

      if (resetToFirstPage) {
        setCurrentPage(1);
      }

      // Calculate skip value
      const pageToUse = resetToFirstPage ? 1 : currentPage;
      const skip = (pageToUse - 1) * itemsPerPage;

      const params = {
        search: searchTerm.trim().length >= 2 ? searchTerm : '',
        department_id: searchByDepartment,
        skip: skip,
        limit: itemsPerPage
      };

      console.log('📡 FETCH FORMS API CALL:', params);

      const res = await getFormListAction(params);

      if (res.success) {
        const formsData = res.data || [];
        const total = res.total || res.totalCount || res.count || 0;

        console.log('🔍 FETCHFORMS - RAW API DATA:', {
          formsDataLength: formsData.length,
          total,
          searchTerm,
          pageToUse
        });

        const transformedForms = formsData.map(form => {
          const formId = form.form_id || form.id || form._id || form.formId;
          return {
            id: formId,
            form_id: formId,
            name: form.name || form.formName || 'Untitled Form',
            departmentName: form.department_name || 'Unknown',
            formNumber: form.form_number || form.formNumber || '',
            isLateEntry: form.isLateEntry || false,
            lateEntryTime: form.lateEntryTime || 0,
            is_active: form.is_active !== false
          };
        });

        console.log('🔄 FETCHFORMS - Setting forms:', transformedForms);
        setFormList(transformedForms);
        setTotalForms(total);
        console.log('✅ Fetch forms success:', { count: transformedForms.length, total });
      } else {
        setFormList([]);
        setTotalForms(0);
        toast.error(res.message || 'Failed to fetch forms');
      }
    } catch (error) {
      console.error('Fetch forms error:', error);
      toast.error('Failed to load forms');
      setFormList([]);
      setTotalForms(0);
    } finally {
      setIsLoading(false);
    }
  }, [searchByDepartment, currentPage, itemsPerPage]);

  const getFormListAPI = useCallback(async (searchBy = '', showLoading = true) => {
    return fetchForms;
  }, [fetchForms]);

  // Initial data load
  useEffect(() => {
    console.log('🎬 COMPONENT MOUNT - Loading initial data');
    fetchForms();
    getDepartmentList();
  }, []);

  // State change logging
  useEffect(() => {
    console.log('🔄 STATE CHANGE - currentPage:', currentPage);
  }, [currentPage]);

  useEffect(() => {
    console.log('🔄 STATE CHANGE - itemsPerPage:', itemsPerPage);
  }, [itemsPerPage]);

  useEffect(() => {
    console.log('🔄 STATE CHANGE - totalForms:', totalForms);
  }, [totalForms]);

  useEffect(() => {
    console.log('🔄 STATE CHANGE - formList length:', formList.length);
  }, [formList]);

  // No longer needed - pagination is handled directly in handlePageChange and handleRowsPerPageChange

  // Simplified debounced search
  const debouncedSearch = useCallback(
    debounce(async (query) => {
      if (query.trim().length < 2 && query.trim().length > 0) {
        return; // Don't search for single characters
      }

      console.log('🔍 DEBOUNCED SEARCH triggered with:', query);
      fetchForms(query, true); // Reset to first page on search
    }, 400),
    [fetchForms]
  );

  const inputHandler = (e) => {
    const { value } = e.target;
    setSearchBy(value);
    debouncedSearch(value);
  };

  const clearSearch = () => {
    setSearchBy('');
    debouncedSearch.cancel();
    fetchForms('', true); // Reset to first page
  };

  const handleDepartmentChange = (e) => {
    setSearchByDepartment(e.target.value);
  };

  // Handle department filter changes
  useEffect(() => {
    console.log('🏢 Department filter changed:', searchByDepartment);
    fetchForms(searchBy, true); // Reset to first page when department changes
  }, [searchByDepartment]);

  // Cleanup debounced search
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const resetFilter = async () => {
    setSearchBy('');
    setSearchByDepartment('');
    debouncedSearch.cancel();
    fetchForms('', true); // Reset to first page
  };

  const inputHandlerAction = (e) => {
    const { name, value } = e.target;
    setformActionData((old) => {
      return { ...old, [name]: value };
    });
  };

  let submitActionButton = async () => {
    try {
      let res = await submitWebFormActionData(formActionData);
      if (res.success) {
        toast.success(res.msg)
        fetchForms(searchBy, false); // Refresh current page
        setIsModelView(false);
      } else {
        toast.error(res.msg)
      }
    } catch (error) {
      console.error('Action submission error:', error);
      toast.error('Failed to execute action');
    }
  };

  // Version management functions
  const handleVersionManagement = async (form) => {
    setSelectedFormForVersions(form);
    setShowVersionModal(true);

    // Load version info for this form
    try {
      const versionInfo = await formTemplateService.getFormVersionInfo(form.form_id);
      if (versionInfo.success) {
        setFormVersionInfo(prev => ({
          ...prev,
          [form.form_id]: versionInfo.data
        }));
      }
    } catch (error) {
      console.error('Error loading version info:', error);
    }
  };

  const handleVersionChange = (newVersion) => {
    // Refresh form list to show updated version info
    getFormListAPI();
    toast.success(`Form version ${newVersion.version} created successfully`);
  };

  const handleVersionSelect = (formId, version) => {
    // Update selected version for the form
    setSelectedVersions(prev => ({
      ...prev,
      [formId]: version
    }));
    console.log(`🔍 Selected version ${version} for form ${formId}`);
  };

  // Load version info for all forms when component mounts - using batch API
  useEffect(() => {
    const loadAllVersionInfo = async () => {
      if (formList.length > 0) {
        try {
          // Extract form IDs for batch request
          const formIds = formList.map(form => form.form_id);

          // Single batch request instead of individual calls
          const result = await formTemplateService.getBulkFormVersionInfo(formIds);

          if (result.success) {
            // result.data is already a map of form_id -> version_info
            setFormVersionInfo(result.data);

            console.log(`✅ Loaded version info for ${result.processed}/${result.total} forms in single request`);
          } else {
            console.error('Failed to load bulk version info:', result.error);
            // Fallback to empty state
            setFormVersionInfo({});
          }
        } catch (error) {
          console.error('Error loading bulk version info:', error);
          // Fallback to empty state
          setFormVersionInfo({});
        }
      }
    };

    loadAllVersionInfo();
  }, [formList]);

  // Removed old DataTable column definitions - using simple table now

  const deleteForm = async (data) => {
    const action = data.is_active ? 'deactivate' : 'activate';
    const msg = data.is_active
      ? `You want to deactivate this form?`
      : `You want to activate this form?`;

    Swal.fire({
      title: "Are you sure?",
      text: msg,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, do it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        // Use dedicated activate/deactivate endpoints
        const formId = data.form_id || data.id;
        let res;

        if (data.is_active) {
          res = await dynamicFormsService.deactivateForm(formId);
        } else {
          res = await dynamicFormsService.activateForm(formId);
        }

        if (res.success) {
          fetchForms(searchBy, false); // Refresh current page after status change
          toast.success(res.message || `Form ${action}d successfully`);
        } else {
          toast.error(res.message || `Failed to ${action} form`);
        }
      }
    });
  };

  // Removed old print columns definition - using simple table now

  const handlePrint = useReactToPrint({
    content: () => allDataRef.current,
  });

  // Export/Import Handlers
  const handleFormSelection = (form, isChecked) => {
    if (isChecked) {
      setSelectedForms(prev => [...prev, form]);
    } else {
      setSelectedForms(prev => prev.filter(f => f.form_id !== form.form_id));
    }
  };

  const handleSelectAll = (isChecked) => {
    if (isChecked) {
      setSelectedForms(formList);
    } else {
      setSelectedForms([]);
    }
  };

  const handleExport = () => {
    if (selectedForms.length === 0) {
      toast.error('Please select at least one form to export');
      return;
    }
    setShowExportModal(true);
  };

  const handleImportSuccess = async () => {
    console.log('🔄 handleImportSuccess called - performing hard refresh');

    // Close the modal first
    setShowImportModal(false);

    // Small delay to show the success toast before page reload
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Perform hard refresh to reload the entire page and show newly imported form
    window.location.reload();
  };

  return (
    <>
      <div className="wrapper">
        <div id="content">
          <Container fluid className="p-4">
            <Row>
              <Col lg={12} md={12} sm={12} className="">
                <div className="formDiv">
                  {/* Modern Header Section */}
                  <Row>
                    <Col lg={12} md={12} sm={12} className="mb-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h3 className="mb-1 text-dark fw-bold">Web Forms</h3>
                          <p className="text-muted mb-0">Manage and create dynamic forms for healthcare data collection</p>
                        </div>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2 px-3"
                            onClick={handlePrint}
                            aria-label="Print form list"
                          >
                            <MdPrint size={16} />
                            Print
                          </button>
                          <button
                            className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2 px-3"
                            onClick={() => setShowImportModal(true)}
                            aria-label="Import form from JSON"
                          >
                            <MdUpload size={16} />
                            Import
                          </button>
                          <button
                            className="btn btn-outline-success btn-sm d-flex align-items-center gap-2 px-3"
                            onClick={handleExport}
                            disabled={selectedForms.length === 0}
                            aria-label="Export selected forms"
                          >
                            <MdDownload size={16} />
                            Export {selectedForms.length > 0 && `(${selectedForms.length})`}
                          </button>
                          <Link to={`${config.baseUrl}createWebForm`} className="text-decoration-none">
                            <button className="btn btn-primary btn-sm d-flex align-items-center gap-2 px-3">
                              <MdAdd size={16} />
                              Add New Form
                            </button>
                          </Link>
                        </div>
                      </div>
                    </Col>
                  </Row>

                  {/* Modern Search Section */}
                  <Row>
                    <Col lg={12} md={12} sm={12} className="mb-4">
                      <div className="card border-0 shadow-sm">
                        <div className="card-body py-3">
                          <Row className="align-items-center">
                            <Col lg={4} md={4} sm={12} className="mb-2 mb-md-0">
                              <div className="position-relative">
                                <MdSearch
                                  className="position-absolute top-50 translate-middle-y text-muted ms-3"
                                  size={18}
                                  style={{ zIndex: 5 }}
                                />
                                <input
                                  type="text"
                                  className="form-control ps-5 pe-5"
                                  placeholder="Search forms as you type..."
                                  value={searchBy}
                                  onChange={inputHandler}
                                  aria-label="Search forms by name"
                                  aria-describedby="search-status"
                                  role="searchbox"
                                  aria-autocomplete="list"
                                  onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                      clearSearch();
                                    }
                                  }}
                                />

                                {/* Loading indicator */}
                                {isLoading && (
                                  <div className="position-absolute top-50 translate-middle-y end-0 me-5">
                                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                                      <span className="visually-hidden">Loading...</span>
                                    </div>
                                  </div>
                                )}

                                {/* Clear button */}
                                {searchBy && !isLoading && (
                                  <button
                                    type="button"
                                    className="btn position-absolute top-50 translate-middle-y end-0 me-2 p-1 border-0 bg-transparent"
                                    onClick={clearSearch}
                                    aria-label="Clear search"
                                    style={{ zIndex: 5 }}
                                  >
                                    <MdClear size={18} className="text-muted" />
                                  </button>
                                )}
                              </div>

                              {/* Search status indicator */}
                              <div id="search-status" className="mt-1" aria-live="polite">
                                {searchBy.trim().length >= 2 && (
                                  <small className="text-muted">
                                    {isLoading ? (
                                      <span>🔍 Searching...</span>
                                    ) : (
                                      <span>📊 Found {totalForms} forms for "{searchBy}"</span>
                                    )}
                                  </small>
                                )}
                                {searchBy.trim().length === 1 && (
                                  <small className="text-muted">💡 Type 2+ characters to search</small>
                                )}
                              </div>
                            </Col>
                            <Col lg={4} md={4} sm={12} className="mb-2 mb-md-0">
                              <select
                                className="form-select"
                                value={searchByDepartment}
                                onChange={handleDepartmentChange}
                                aria-label="Filter by department"
                              >
                                <option value="">All Departments</option>
                                {departmentList.map((dept, index) => (
                                  <option key={dept.department_id || index} value={dept.department_id}>
                                    {dept.name}
                                  </option>
                                ))}
                              </select>
                            </Col>
                            <Col lg={4} md={4} sm={12}>
                              <div className="d-flex gap-2 justify-content-md-end">
                                <button
                                  className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2 px-3"
                                  onClick={resetFilter}
                                  aria-label="Clear all filters"
                                >
                                  <MdRefresh size={16} />
                                  Reset All
                                </button>
                              </div>
                            </Col>
                          </Row>
                        </div>
                      </div>
                    </Col>
                  </Row>

                  {/* Modern Table Section */}
                  <Row>
                    <Col lg={12} md={12} sm={12} className="">
                      <div className="card border-0 shadow-sm position-relative">
                        <div className="card-header bg-white border-bottom-0 py-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0 fw-semibold text-dark">
                              Forms List ({totalForms} total)
                            </h6>
                            <small className="text-muted">
                              Click on any form to view details
                            </small>
                          </div>
                        </div>
                        <div className="card-body p-0">
                          {/* Custom Table */}
                          {formList.length > 0 ? (
                            <>
                              <Table striped hover responsive className="mb-0">
                                <thead className="bg-light">
                                  <tr>
                                    <th className="px-3 py-3 fw-semibold text-muted" style={{ width: '40px' }}>
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={formList.length > 0 && selectedForms.length === formList.length}
                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                        aria-label="Select all forms"
                                      />
                                    </th>
                                    <th className="px-3 py-3 fw-semibold text-muted" style={{ width: '60px' }}>#</th>
                                    <th className="px-3 py-3 fw-semibold text-muted" style={{ minWidth: '200px' }}>Form Name</th>
                                    <th className="px-3 py-3 fw-semibold text-muted" style={{ width: '200px' }}>Form Number</th>
                                    <th className="px-3 py-3 fw-semibold text-muted" style={{ width: '100px' }}>Version</th>
                                    <th className="px-3 py-3 fw-semibold text-muted" style={{ width: '100px' }}>Status</th>
                                    <th className="px-3 py-3 fw-semibold text-muted text-center" style={{ width: '180px' }}>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {formList.map((row, index) => {
                                    const globalIndex = (currentPage - 1) * itemsPerPage + index + 1;
                                    const isSelected = selectedForms.some(f => f.form_id === row.form_id);
                                    return (
                                      <tr key={row.id || index} style={{ minHeight: '60px' }}>
                                        <td className="px-3 py-3 align-middle">
                                          <input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={isSelected}
                                            onChange={(e) => handleFormSelection(row, e.target.checked)}
                                            aria-label={`Select ${row.name}`}
                                          />
                                        </td>
                                        <td className="px-3 py-3 align-middle">{globalIndex}</td>
                                        <td className="px-3 py-3 align-middle">
                                          <div className="d-flex flex-column">
                                            <span className="fw-medium text-dark" style={{ fontSize: '14px' }}>
                                              {row.name || 'Untitled Form'}
                                            </span>
                                            <small className="text-muted" style={{ fontSize: '12px' }}>
                                              {row.departmentName || 'No Department'}
                                            </small>
                                          </div>
                                        </td>
                                        <td className="px-3 py-3 align-middle">
                                          <OverlayTrigger
                                            placement="top"
                                            overlay={<Tooltip>{row.form_id || 'No Form ID'}</Tooltip>}
                                          >
                                            <span className="font-monospace text-muted" style={{ fontSize: '12px', cursor: 'pointer' }}>
                                              {row.form_id ? row.form_id.substring(0, 20) + '...' : 'N/A'}
                                            </span>
                                          </OverlayTrigger>
                                        </td>
                                        <td className="px-3 py-3 align-middle text-center">
                                          {(() => {
                                            const versionInfo = formVersionInfo[row.form_id];
                                            if (!versionInfo) {
                                              return (
                                                <Badge
                                                  bg="light"
                                                  text="dark"
                                                  className="px-2 py-1"
                                                  style={{
                                                    fontSize: '10px',
                                                    display: 'inline-block',
                                                    padding: '2px 8px',
                                                    borderRadius: '3px',
                                                    fontWeight: '500'
                                                  }}
                                                >
                                                  v1
                                                </Badge>
                                              );
                                            }

                                            const hasMultipleVersions = versionInfo.total_versions > 1;
                                            return (
                                              <div className="d-flex flex-column align-items-center">
                                                <Badge
                                                  bg={hasMultipleVersions ? 'info' : 'light'}
                                                  text={hasMultipleVersions ? 'white' : 'dark'}
                                                  className="px-2 py-1"
                                                  style={{
                                                    fontSize: '10px',
                                                    display: 'inline-block',
                                                    padding: '2px 8px',
                                                    borderRadius: '3px',
                                                    fontWeight: '500'
                                                  }}
                                                >
                                                  {versionInfo.latest_version}
                                                </Badge>
                                                {hasMultipleVersions && (
                                                  <small className="text-muted" style={{ fontSize: '9px' }}>
                                                    {versionInfo.total_versions} versions
                                                  </small>
                                                )}
                                              </div>
                                            );
                                          })()}
                                        </td>
                                        <td className="px-3 py-3 align-middle">
                                          <Badge
                                            bg={row.is_active ? 'success' : 'secondary'}
                                            className="px-2 py-1"
                                            style={{ fontSize: '11px' }}
                                          >
                                            {row.is_active ? 'Active' : 'Inactive'}
                                          </Badge>
                                        </td>
                                        <td className="px-3 py-3 align-middle text-center">
                                          <div className="d-flex align-items-center justify-content-center gap-2">
                                            {(row.form_id || row.id) ? (
                                              <>
                                                <OverlayTrigger
                                                  placement="top"
                                                  overlay={<Tooltip>View Form Structure</Tooltip>}
                                                >
                                                  <Link
                                                    to={`${config.baseUrl}formDetails/${row.form_id || row.id}`}
                                                    className="btn btn-outline-primary btn-sm p-1"
                                                    style={{ width: '32px', height: '32px' }}
                                                  >
                                                    <MdVisibility size={16} />
                                                  </Link>
                                                </OverlayTrigger>

                                                <OverlayTrigger
                                                  placement="top"
                                                  overlay={<Tooltip>Edit Form</Tooltip>}
                                                >
                                                  <Link
                                                    to={`${config.baseUrl}editWebForm/${row.form_id || row.id}?version=${selectedVersions[row.form_id || row.id] || formVersionInfo[row.form_id || row.id]?.latest_version || '1'}`}
                                                    className="btn btn-outline-secondary btn-sm p-1"
                                                    style={{ width: '32px', height: '32px' }}
                                                  >
                                                    <MdEdit size={16} />
                                                  </Link>
                                                </OverlayTrigger>

                                                <OverlayTrigger
                                                  placement="top"
                                                  overlay={<Tooltip>Manage Versions</Tooltip>}
                                                >
                                                  <button
                                                    className="btn btn-outline-info btn-sm p-1"
                                                    style={{ width: '32px', height: '32px' }}
                                                    onClick={() => handleVersionManagement(row)}
                                                  >
                                                    <BranchesOutlined style={{ fontSize: '14px' }} />
                                                  </button>
                                                </OverlayTrigger>

                                                <OverlayTrigger
                                                  placement="top"
                                                  overlay={<Tooltip>{row.is_active ? 'Deactivate Form' : 'Activate Form'}</Tooltip>}
                                                >
                                                  <button
                                                    className={`btn btn-outline-${row.is_active ? 'warning' : 'success'} btn-sm p-1`}
                                                    style={{ width: '32px', height: '32px' }}
                                                    onClick={() => deleteForm(row)}
                                                  >
                                                    {row.is_active ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                                                  </button>
                                                </OverlayTrigger>

                                                <OverlayTrigger
                                                  placement="top"
                                                  overlay={<Tooltip>Add Fields</Tooltip>}
                                                >
                                                  <button
                                                    className="btn btn-outline-info btn-sm p-1"
                                                    style={{ width: '32px', height: '32px' }}
                                                    onClick={() => { handleOpenView(row) }}
                                                  >
                                                    <BsPlus size={16} />
                                                  </button>
                                                </OverlayTrigger>
                                              </>
                                            ) : (
                                              <span className="text-muted small">Invalid ID</span>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </Table>

                              {/* Custom Pagination */}
                              <CustomPagination
                                currentPage={currentPage}
                                totalItems={totalForms}
                                itemsPerPage={itemsPerPage}
                                onPageChange={handlePageChange}
                                onItemsPerPageChange={handleItemsPerPageChange}
                                loading={isLoading}
                              />
                            </>
                          ) : (
                            <div className="text-center py-5">
                              <div className="text-muted">
                                <MdVisibility size={48} className="mb-3" />
                                <h6>No forms found</h6>
                                <p className="mb-0">Try adjusting your search criteria or create a new form.</p>
                                {isLoading && (
                                  <div className="mt-3">
                                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                                      <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <span className="ms-2 text-muted">Loading forms...</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Loading overlay */}
                        {isLoading && formList.length > 0 && (
                          <div className="position-absolute top-50 start-50 translate-middle" style={{ zIndex: 10 }}>
                            <div className="d-flex align-items-center gap-2 bg-white px-3 py-2 rounded shadow-sm border">
                              <div className="spinner-border spinner-border-sm text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                              <span className="text-muted fw-medium">Loading forms...</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </div>

      {/* Print Section */}
      <div style={{ display: 'none' }}>
        <div ref={allDataRef}>
          <h3>Form List Report</h3>
          <Table striped responsive>
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Department</th>
                <th>Form Number</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {formList.map((row, index) => (
                <tr key={row.id || index}>
                  <td>{index + 1}</td>
                  <td>{row.name}</td>
                  <td>{row.departmentName}</td>
                  <td>{row.form_id}</td>
                  <td>{row.is_active ? 'Active' : 'Inactive'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Modern Modal for Form Actions */}
      <Modal
        show={isModelView}
        onHide={handleViewClose}
        size="lg"
        centered
        className="modern-modal"
      >
        <Modal.Header closeButton className="border-0 pb-2">
          <Modal.Title className="h5 fw-semibold text-dark d-flex align-items-center gap-2">
            <BsPlus className="text-primary" size={24} />
            Form Actions
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 py-3">
          <div className="mb-3 p-3 bg-light rounded-3">
            <small className="text-muted">Selected Form</small>
            <h6 className="mb-0 text-dark fw-medium">
              {formActionData.name || 'No form selected'}
            </h6>
          </div>

          <Row>
            <Col lg={12} className="mb-3">
              <label className="form-label text-sm fw-medium text-dark">
                Choose Action <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                name="action"
                value={formActionData.action || ''}
                onChange={inputHandlerAction}
                aria-label="Select form action"
              >
                <option value="">Select an action...</option>
                <option value="edit">Edit Form</option>
                <option value="delete">Delete Form</option>
                <option value="duplicate">Duplicate Form</option>
              </select>
            </Col>
          </Row>

          {formActionData.action && (
            <div className="alert alert-info border-0 bg-info bg-opacity-10">
              <small>
                <strong>Action:</strong> {
                  formActionData.action === 'edit' ? 'This will open the form editor' :
                    formActionData.action === 'delete' ? 'This will permanently delete the form' :
                      formActionData.action === 'duplicate' ? 'This will create a copy of this form' : ''
                }
              </small>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-2">
          <div className="d-flex gap-2 w-100 justify-content-end">
            <button
              className="btn btn-outline-secondary btn-sm px-4"
              onClick={handleViewClose}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary btn-sm px-4"
              onClick={submitActionButton}
              disabled={!formActionData.action}
            >
              Execute Action
            </button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Version Management Modal */}
      <Modal
        show={showVersionModal}
        onHide={() => setShowVersionModal(false)}
        size="lg"
        centered
        style={{ zIndex: 1050 }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Form Version Management - {selectedFormForVersions?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedFormForVersions && (
            <FormVersionSelector
              formId={selectedFormForVersions.form_id}
              currentVersion={formVersionInfo[selectedFormForVersions.form_id]?.latest_version || '1'}
              onVersionChange={handleVersionChange}
              onVersionSelect={handleVersionSelect}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Export Form Modal */}
      <ExportFormModal
        show={showExportModal}
        onHide={() => {
          setShowExportModal(false);
          setSelectedForms([]);
        }}
        selectedForms={selectedForms}
      />

      {/* Import Form Modal */}
      <ImportFormModal
        show={showImportModal}
        onHide={() => setShowImportModal(false)}
        onImportSuccess={handleImportSuccess}
      />

      <Toaster />
    </>
  );
};

export default Webform;
